import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import type { Brand } from '@/components/finance/BrandSelector';
import { addDays, subDays, startOfDay, format, startOfWeek, endOfWeek, isWithinInterval, getDate, getMonth, getYear, differenceInWeeks, parseISO, getDay, setDay } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { parseDate } from '@/lib/localizedDateUtils';
import { calculateMonthlySalaryCost, getDayOfWeekName } from '@/lib/salaryCalculations';

// Taxa de câmbio padrão BRL -> USD (fallback se não houver taxa registrada)
const DEFAULT_BRL_TO_USD_RATE = 5.5;

// Função helper para converter BRL para USD
const convertBRLtoUSD = (amountBRL: number, rate: number = DEFAULT_BRL_TO_USD_RATE): number => amountBRL / rate;

export interface CashFlowMovement {
  id: string;
  date: string;
  type: 'entrada' | 'saida';
  description: string;
  amount: number;
  category: string;
  reference?: string;
}

export interface WeeklyDetails {
  recebiveisAVencer: Array<{ cliente: string; valor: number; vencimento: string }>;
  recebiveisEmAtraso: Array<{ cliente: string; valor: number; diasAtraso: number }>;
  despesasFixas: Array<{ nome: string; valor: number; moeda: string; valorUSD: number }>;
  funcionarios: Array<{ nome: string; cargo: string; custoMensal: number; tipoPagamento: string; diaPagamento: string; custoSemana: number }>;
  // Detalhes de novas vendas com parcelamento
  novasVendas: {
    metaMensal: number;
    metaSemanal: number;
    distribuicaoMes: number;           // % do contrato que entra neste mês
    taxaRecebimento: number;           // taxa aplicada (curva de recebimento)
    valorProjetado: number;            // valor final projetado
    fonteCalculo: 'dre_projetado' | 'media_historica';
    // Breakdown por origem
    breakdown: {
      dealsFechadosEsteMes: number;    // Primeira parcela de deals novos
      parcelasDealsAnteriores: number; // Parcelas de deals de meses anteriores
    };
  };
  metricas: {
    curvaRecebimento: { semana0: number; semana1: number; semana2: number; semana3: number };
    taxaRecebimentoAtraso: number;
    taxaCambio: number;
    vendaNovaBase: number;
    usandoDreProjetado: boolean;
    impostoAliquota: number;
    parcelamento: {
      mediaParcelasPorDeal: number;
      distribuicaoRecebimento: { mes0: number; mes1: number; mes2: number; mes3: number; mes4: number; mes5: number; mes6: number };
    };
  };
  totais: {
    totalAVencerOriginal: number;
    totalAVencerProjetado: number;
    totalAtrasoOriginal: number;
    totalAtrasoProjetado: number;
    totalDespesasFixasMensal: number;
    totalDespesasFixasSemanal: number;
    totalFolhaMensal: number;
    totalFolhaSemanal: number;
    totalImpostos: number;
  };
}

export interface WeeklyProjection {
  weekStart: string;
  weekEnd: string;
  weekLabel: string;
  saldoInicial: number;
  entradasAVencer: number;
  entradasAtraso40: number;
  entradasNovasVendas: number;
  totalEntradas: number;
  usandoDreProjetado: boolean;
  saidasFolha: number;
  saidasFixas: number;
  saidasFornecedores: number;
  saidasImpostos: number;
  totalSaidas: number;
  saldoFinal: number;
  details: WeeklyDetails;
}

export interface CashFlowAlert {
  type: 'warning' | 'danger' | 'info';
  message: string;
  week?: number;
}

export interface CashFlowRecommendation {
  icon: string;
  message: string;
}

// Hook para movimentações realizadas (histórico)
export function useCashFlowRealized(
  businessUnit: BusinessUnit = 'all',
  brand: Brand = 'all',
  daysBack: number = 30
) {
  return useQuery({
    queryKey: ['cash-flow-realized', businessUnit, brand, daysBack],
    queryFn: async () => {
      const today = new Date();
      const startDate = subDays(today, daysBack);
      
      // 1. Buscar pagamentos recebidos (entradas)
      let paymentsQuery = supabase
        .from('payments')
        .select(`
          id,
          paid_at,
          paid_amount,
          description,
          deals!inner(business_unit, brand, contact_id, contacts(full_name))
        `)
        .eq('status', 'paid')
        .gte('paid_at', safeFormat(startDate, 'yyyy-MM-dd'))
        .order('paid_at', { ascending: false });

      if (businessUnit !== 'all') {
        paymentsQuery = paymentsQuery.eq('deals.business_unit', businessUnit);
      }
      if (brand !== 'all') {
        paymentsQuery = paymentsQuery.eq('deals.brand', brand);
      }

      const { data: payments } = await paymentsQuery;

      // 2. Buscar saldo inicial (soma das contas bancárias)
      const { data: bankData } = await supabase
        .from('bank_accounts')
        .select('current_balance, initial_balance')
        .eq('is_active', true);

      const saldoAtual = bankData?.reduce((sum, acc) => sum + (acc.current_balance || 0), 0) || 0;

      // 3. Montar movimentações
      const movements: CashFlowMovement[] = [];

      // Entradas dos pagamentos
      payments?.forEach(p => {
        const deal = p.deals as any;
        movements.push({
          id: p.id,
          date: p.paid_at || '',
          type: 'entrada',
          description: deal?.contacts?.full_name || p.description || 'Pagamento',
          amount: p.paid_amount || 0,
          category: 'Recebimento de cliente',
          reference: p.description,
        });
      });

      // Calcular totais
      const totalEntradas = movements
        .filter(m => m.type === 'entrada')
        .reduce((sum, m) => sum + m.amount, 0);
      
      const totalSaidas = movements
        .filter(m => m.type === 'saida')
        .reduce((sum, m) => sum + m.amount, 0);

      // Saldo inicial estimado
      const saldoInicial = saldoAtual - totalEntradas + totalSaidas;

      return {
        saldoInicial,
        saldoFinal: saldoAtual,
        totalEntradas,
        totalSaidas,
        variacao: saldoAtual - saldoInicial,
        movements: movements.sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        ),
      };
    },
    staleTime: 60000,
  });
}

// Hook para projeção semanal com métricas dinâmicas
export function useCashFlowProjection(
  businessUnit: BusinessUnit = 'all',
  brand: Brand = 'all',
  weeksAhead: number = 4
) {
  return useQuery({
    queryKey: ['cash-flow-projection-weekly', businessUnit, brand, weeksAhead],
    queryFn: async () => {
      const today = startOfDay(new Date());
      
      // 0. Buscar taxa de câmbio mais recente da tabela exchange_rates
      const { data: latestRate } = await supabase
        .from('exchange_rates')
        .select('rate')
        .eq('from_currency', 'BRL')
        .eq('to_currency', 'USD')
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      // Taxa de câmbio: se existir na tabela, usar (1/rate porque rate é BRL->USD como 0.18)
      // Senão, usar taxa padrão de 5.5
      const exchangeRate = latestRate?.rate ? (1 / latestRate.rate) : DEFAULT_BRL_TO_USD_RATE;
      
      // 0.5. Calcular métricas de pagamento históricas
      const metrics = await calculatePaymentMetrics(businessUnit, brand);
      
      // 1. Saldo atual das contas bancárias
      const { data: bankData } = await supabase
        .from('bank_accounts')
        .select('current_balance')
        .eq('is_active', true);
      
      const saldoAtual = bankData?.reduce((sum, acc) => sum + (acc.current_balance || 0), 0) || 0;

      // 2. Buscar pagamentos a vencer com data e cliente
      let receivablesQuery = supabase
        .from('v_payments_aging_detail')
        .select('valor_aberto, dias_atraso, due_date, cliente');

      if (businessUnit !== 'all') {
        receivablesQuery = receivablesQuery.eq('business_unit', businessUnit);
      }
      if (brand !== 'all') {
        receivablesQuery = receivablesQuery.eq('brand', brand);
      }

      const { data: receivables } = await receivablesQuery;

      // 3. Buscar despesas recorrentes REAIS (com moeda)
      let expensesQuery = supabase
        .from('recurring_expenses')
        .select('amount, day_of_month, name, account_code, currency')
        .eq('is_active', true);
      
      if (businessUnit !== 'all') {
        expensesQuery = expensesQuery.eq('business_unit', businessUnit);
      }

      const { data: expenses } = await expensesQuery;

      // 4. Buscar folha de pagamento REAL dos employees (com todos os campos de salário)
      let employeesQuery = supabase
        .from('employees')
        .select('full_name, role, base_salary, salary_currency, business_unit, salary_frequency, weekly_payment_type, hourly_rate, projected_hours_per_week, payment_day_of_week')
        .eq('status', 'active');
      
      // Filtrar por business_unit se não for 'all'
      if (businessUnit !== 'all') {
        employeesQuery = employeesQuery.eq('business_unit', businessUnit);
      }

      const { data: employees } = await employeesQuery;

      // Current month for salary calculation
      const currentYear = getYear(today);
      const currentMonth = getMonth(today);

      // Separar funcionários por frequência de pagamento
      const funcionariosMensais = employees?.filter(e => 
        e.salary_frequency === 'monthly' || !e.salary_frequency
      ) || [];
      
      const funcionariosSemanais = employees?.filter(e => 
        e.salary_frequency === 'weekly'
      ) || [];

      // Calcular folha total (para referência)
      let folhaUSD = 0;
      let folhaBRL = 0;
      
      employees?.forEach(emp => {
        const monthlyCost = calculateMonthlySalaryCost(emp, currentYear, currentMonth);
        if (emp.salary_currency === 'USD') {
          folhaUSD += monthlyCost;
        } else {
          folhaBRL += monthlyCost;
        }
      });
      
      const folhaTotal = folhaUSD + convertBRLtoUSD(folhaBRL, exchangeRate);

      // Função auxiliar para calcular custo semanal de um funcionário
      const calcularCustoSemanal = (emp: typeof employees extends (infer U)[] | null ? U : never) => {
        if (!emp) return 0;
        
        const frequency = emp.salary_frequency || 'monthly';
        
        // Para funcionários SEMANAIS, calcular o valor semanal real (não o mensal!)
        if (frequency === 'weekly') {
          let weeklyValue = 0;
          
          if (emp.weekly_payment_type === 'hourly') {
            // Hora × horas/semana
            const hourlyRate = Number(emp.hourly_rate) || 0;
            const hoursPerWeek = Number(emp.projected_hours_per_week) || 0;
            weeklyValue = hourlyRate * hoursPerWeek;
          } else {
            // Valor fixo semanal (base_salary para semanais é o valor semanal)
            weeklyValue = Number(emp.base_salary) || 0;
          }
          
          // Converter para USD se necessário
          return emp.salary_currency === 'BRL' 
            ? convertBRLtoUSD(weeklyValue, exchangeRate) 
            : weeklyValue;
        }
        
        // Para MENSAIS/QUINZENAIS, usar o custo mensal (será chamado apenas na semana de pagamento)
        const monthlyCost = calculateMonthlySalaryCost(emp, currentYear, currentMonth);
        return emp.salary_currency === 'BRL' 
          ? convertBRLtoUSD(monthlyCost, exchangeRate) 
          : monthlyCost;
      };

      // 5. Buscar receita projetada do DRE (budget_entries com tipo revenue)
      const budgetMonth = currentMonth + 1; // budget uses 1-indexed month
      
      // Buscar budget entries de receita para os próximos meses
      const { data: budgetRevenue } = await supabase
        .from('budget_entries')
        .select(`
          planned_amount,
          year,
          month,
          chart_of_accounts!budget_entries_account_code_fkey(type)
        `)
        .eq('year', currentYear)
        .gte('month', budgetMonth)
        .lte('month', budgetMonth + Math.ceil(weeksAhead / 4));

      // Filtrar apenas entradas de receita (revenue)
      const revenueEntries = budgetRevenue?.filter(b => 
        (b.chart_of_accounts as any)?.type === 'revenue'
      ) || [];
      
      // Calcular receita mensal projetada total
      const totalReceitaProjetadaMensal = revenueEntries.reduce(
        (sum, b) => sum + (b.planned_amount || 0), 0
      );
      
      // Receita mensal para cálculo (por mês, não semanal diretamente)
      const mesesCobertos = Math.max(1, Math.ceil(weeksAhead / 4));
      const receitaMensalProjetada = totalReceitaProjetadaMensal / mesesCobertos;
      
      // Flag para indicar se está usando DRE projetado
      const usandoDreProjetado = receitaMensalProjetada > 0;

      // Fallback: usar média semanal do histórico × 4 para ter mensal
      const avgMonthlySales = usandoDreProjetado ? 0 : metrics.mediaSemanalRecebida * 4;
      
      // Meta mensal de vendas (base para cálculo de novas vendas)
      const metaVendasMensal = usandoDreProjetado ? receitaMensalProjetada : avgMonthlySales;
      
      // Distribuição de parcelamento (baseada em dados reais)
      // Isso define quanto % do valor de um contrato entra em cada mês
      const distribuicao = {
        mes0: 0.20,  // 20% no mês do fechamento (primeira parcela)
        mes1: 0.11,  // 11% no mês 1
        mes2: 0.10,  // 10% no mês 2
        mes3: 0.08,  // 8% no mês 3
        mes4: 0.08,  // 8% no mês 4
        mes5: 0.07,  // 7% no mês 5
        mes6: 0.36,  // 36% restante (meses 6+)
      };

      // 6. Separar a vencer vs em atraso e aplicar curva de recebimento
      const aVencerTotal = receivables?.filter(r => (r.dias_atraso || 0) <= 0)
        .reduce((sum, r) => sum + (r.valor_aberto || 0), 0) || 0;
      const emAtrasoTotal = receivables?.filter(r => (r.dias_atraso || 0) > 0)
        .reduce((sum, r) => sum + (r.valor_aberto || 0), 0) || 0;

      // Taxa dinâmica baseada no histórico (cenário realista)
      const taxaRecebimentoAVencer = metrics.curvaRecebimento.semana0 + metrics.curvaRecebimento.semana1;
      const taxaRecebimentoEmAtraso = metrics.cenarios.realista.taxaRecebimento * 0.7; // Em atraso tem taxa menor

      // 7. Calcular projeção por semana usando curva de recebimento
      const weeks: WeeklyProjection[] = [];
      let saldoAnterior = saldoAtual;

      // Pré-calcular recebíveis por semana de vencimento
      const recebivelPorSemana: number[] = new Array(weeksAhead).fill(0);
      receivables?.forEach(r => {
        if (!r.due_date) return;
        const dueDate = parseISO(r.due_date);
        const weekDiff = differenceInWeeks(dueDate, today);
        
        // Se vence nas próximas semanas
        if (weekDiff >= 0 && weekDiff < weeksAhead && (r.dias_atraso || 0) <= 0) {
          recebivelPorSemana[weekDiff] += r.valor_aberto || 0;
        }
      });

      // Pré-calcular despesas fixas mensais (distribuir por 4 semanas)
      const totalDespesasUSD = expenses?.filter(e => e.currency === 'USD')
        .reduce((sum, e) => sum + (e.amount || 0), 0) || 0;
      const totalDespesasBRL = expenses?.filter(e => e.currency === 'BRL')
        .reduce((sum, e) => sum + (e.amount || 0), 0) || 0;
      const despesasFixasPorSemana = (totalDespesasUSD + convertBRLtoUSD(totalDespesasBRL, exchangeRate)) / 4;

      for (let i = 0; i < weeksAhead; i++) {
        const weekStart = addDays(startOfWeek(today, { weekStartsOn: 1 }), i * 7);
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

        // Entradas a vencer nesta semana com curva de recebimento dinâmica
        let entradasAVencerSemana = 0;
        
        // Recebíveis que vencem nesta semana - aplicar curva
        const venceNestaSemana = recebivelPorSemana[i] || 0;
        entradasAVencerSemana += venceNestaSemana * metrics.curvaRecebimento.semana0;
        
        // Recebíveis que venceram na semana anterior - alguns pagam com atraso
        if (i > 0) {
          entradasAVencerSemana += (recebivelPorSemana[i - 1] || 0) * metrics.curvaRecebimento.semana1;
        }
        if (i > 1) {
          entradasAVencerSemana += (recebivelPorSemana[i - 2] || 0) * metrics.curvaRecebimento.semana2;
        }
        if (i > 2) {
          entradasAVencerSemana += (recebivelPorSemana[i - 3] || 0) * metrics.curvaRecebimento.semana3;
        }

        // Entradas em atraso distribuídas com taxa dinâmica
        const atraso40PorSemana = (emAtrasoTotal * taxaRecebimentoEmAtraso) / weeksAhead;

        // ===== NOVAS VENDAS COM PARCELAMENTO REAL =====
        // Em vez de adicionar receita semanal diretamente, calculamos:
        // 1. Deals fechados ESTE mês: aplicar distribuição de parcelamento
        // 2. Parcelas de deals de meses anteriores: já estão nos receivables
        
        // Qual semana do mês estamos?
        const weekOfMonth = Math.floor(getDate(weekStart) / 7);
        
        // Meta semanal = meta mensal / 4 semanas
        const metaSemanal = metaVendasMensal / 4;
        
        // Do valor semanal de vendas novas, quanto entra nesta semana?
        // Aplicar a distribuição de parcelamento:
        // - Primeira parcela (mes0): entra imediatamente com curva de recebimento
        // - Parcelas futuras: entrarão nos meses seguintes (já serão receivables)
        
        // Valor da primeira parcela de vendas desta semana
        const primeiraParcelaSemana = metaSemanal * distribuicao.mes0;
        
        // Aplicar curva de recebimento (nem todos pagam a primeira parcela em dia)
        const taxaRecebimentoPrimeiraParcela = metrics.curvaRecebimento.semana0;
        
        // Parcelas de vendas de semanas anteriores deste mês que atrasaram
        let parcelasAtrasadasDeVendasNovas = 0;
        if (i > 0) {
          // Semana anterior - alguns pagam a primeira parcela com 1 semana de atraso
          parcelasAtrasadasDeVendasNovas += (metaSemanal * distribuicao.mes0) * metrics.curvaRecebimento.semana1;
        }
        if (i > 1) {
          parcelasAtrasadasDeVendasNovas += (metaSemanal * distribuicao.mes0) * metrics.curvaRecebimento.semana2;
        }
        
        // Total de novas vendas = primeira parcela em dia + atrasadas
        const entradasNovasVendas = (primeiraParcelaSemana * taxaRecebimentoPrimeiraParcela) + parcelasAtrasadasDeVendasNovas;

        const totalEntradas = entradasAVencerSemana + atraso40PorSemana + entradasNovasVendas;

        // Saídas - despesas fixas mensais distribuídas por semana
        const despesasFixasSemana = despesasFixasPorSemana;

        // ===== FOLHA DE PAGAMENTO POR DIA DE PAGAMENTO INDIVIDUAL =====
        let saidasFolha = 0;
        const funcionariosQuePagamNestaSemana: Array<{
          nome: string;
          cargo: string;
          custoMensal: number;
          tipoPagamento: string;
          diaPagamento: string;
          custoSemana: number;
        }> = [];

        // 1. FUNCIONÁRIOS SEMANAIS - pagam toda semana no dia específico
        funcionariosSemanais.forEach(emp => {
          const diaPagamento = emp.payment_day_of_week ?? 5; // Default sexta (5)
          // Calcular a data do dia de pagamento nesta semana
          const dataPagamentoSemana = setDay(weekStart, diaPagamento, { weekStartsOn: 1 });
          
          // Verificar se o dia de pagamento cai nesta semana
          if (isWithinInterval(dataPagamentoSemana, { start: weekStart, end: weekEnd })) {
            const custoSemanal = calcularCustoSemanal(emp);
            saidasFolha += custoSemanal;
            
            funcionariosQuePagamNestaSemana.push({
              nome: emp.full_name || 'N/A',
              cargo: emp.role || 'N/A',
              custoMensal: calculateMonthlySalaryCost(emp, currentYear, currentMonth),
              tipoPagamento: 'Semanal',
              diaPagamento: getDayOfWeekName(diaPagamento),
              custoSemana: custoSemanal,
            });
          }
        });

        // 2. FUNCIONÁRIOS MENSAIS - pagam no dia específico do mês (default: 25)
        const dayOfMonthStartFolha = getDate(weekStart);
        const dayOfMonthEndFolha = getDate(weekEnd);
        
        funcionariosMensais.forEach(emp => {
          // Usar dia 25 como padrão se não tiver configurado
          const diaPagamentoMes = 25; // TODO: adicionar campo payment_day_of_month na tabela
          
          // Verificar se o dia de pagamento está nesta semana
          const pagaNestaSemana = 
            (dayOfMonthStartFolha <= diaPagamentoMes && dayOfMonthEndFolha >= diaPagamentoMes) ||
            (dayOfMonthStartFolha > dayOfMonthEndFolha && (dayOfMonthStartFolha <= diaPagamentoMes || dayOfMonthEndFolha >= diaPagamentoMes));
          
          if (pagaNestaSemana) {
            const monthlyCost = calculateMonthlySalaryCost(emp, currentYear, currentMonth);
            const custoMensalUSD = emp.salary_currency === 'BRL' 
              ? convertBRLtoUSD(monthlyCost, exchangeRate) 
              : monthlyCost;
            
            saidasFolha += custoMensalUSD;
            
            funcionariosQuePagamNestaSemana.push({
              nome: emp.full_name || 'N/A',
              cargo: emp.role || 'N/A',
              custoMensal: monthlyCost,
              tipoPagamento: 'Mensal',
              diaPagamento: `Dia ${diaPagamentoMes}`,
              custoSemana: custoMensalUSD,
            });
          }
        });

        // Fornecedores - não temos dados, então usar 0
        const saidasFornecedores = 0;

        // Impostos - estimar como 15% da receita mensal, distribuído no dia 20
        const dayOfMonthEnd = getDate(weekEnd);
        const dayOfMonthStart = getDate(weekStart);
        const hasDay20 = (dayOfMonthStart <= 20 && dayOfMonthEnd >= 20) || 
                         (dayOfMonthStart > dayOfMonthEnd && (dayOfMonthStart <= 20 || dayOfMonthEnd >= 20));
        const saidasImpostos = hasDay20 ? (metrics.mediaSemanalRecebida * 4 * 0.15) : 0;

        const totalSaidas = saidasFolha + despesasFixasSemana + saidasFornecedores + saidasImpostos;

        const saldoFinal = saldoAnterior + totalEntradas - totalSaidas;

        // Construir detalhes para debugging
        const recebiveisAVencerDetalhes = receivables
          ?.filter(r => {
            if (!r.due_date) return false;
            const dueDate = parseISO(r.due_date);
            const weekDiff = differenceInWeeks(dueDate, today);
            return weekDiff === i && (r.dias_atraso || 0) <= 0;
          })
          .map(r => ({
            cliente: r.cliente || 'N/A',
            valor: r.valor_aberto || 0,
            vencimento: r.due_date ? safeFormat(parseISO(r.due_date), 'dd/MM') : 'N/A',
          })) || [];

        const recebiveisEmAtrasoDetalhes = i === 0 ? (receivables
          ?.filter(r => (r.dias_atraso || 0) > 0)
          .map(r => ({
            cliente: r.cliente || 'N/A',
            valor: r.valor_aberto || 0,
            diasAtraso: r.dias_atraso || 0,
          })) || []) : [];

        const despesasFixasDetalhes = expenses?.map(e => ({
          nome: e.name || 'Sem nome',
          valor: e.amount || 0,
          moeda: e.currency || 'USD',
          valorUSD: e.currency === 'BRL' ? convertBRLtoUSD(e.amount || 0, exchangeRate) : (e.amount || 0),
        })) || [];

        const funcionariosDetalhes = funcionariosQuePagamNestaSemana;

        const weekDetails: WeeklyDetails = {
          recebiveisAVencer: recebiveisAVencerDetalhes,
          recebiveisEmAtraso: recebiveisEmAtrasoDetalhes,
          despesasFixas: despesasFixasDetalhes,
          funcionarios: funcionariosDetalhes,
          novasVendas: {
            metaMensal: metaVendasMensal,
            metaSemanal: metaSemanal,
            distribuicaoMes: distribuicao.mes0,
            taxaRecebimento: taxaRecebimentoPrimeiraParcela,
            valorProjetado: entradasNovasVendas,
            fonteCalculo: usandoDreProjetado ? 'dre_projetado' : 'media_historica',
            breakdown: {
              dealsFechadosEsteMes: primeiraParcelaSemana * taxaRecebimentoPrimeiraParcela,
              parcelasDealsAnteriores: parcelasAtrasadasDeVendasNovas,
            },
          },
          metricas: {
            curvaRecebimento: metrics.curvaRecebimento,
            taxaRecebimentoAtraso: taxaRecebimentoEmAtraso,
            taxaCambio: exchangeRate,
            vendaNovaBase: metaSemanal,
            usandoDreProjetado,
            impostoAliquota: 0.15,
            parcelamento: {
              mediaParcelasPorDeal: 13.4,
              distribuicaoRecebimento: distribuicao,
            },
          },
          totais: {
            totalAVencerOriginal: venceNestaSemana,
            totalAVencerProjetado: Math.round(entradasAVencerSemana),
            totalAtrasoOriginal: emAtrasoTotal,
            totalAtrasoProjetado: Math.round(atraso40PorSemana),
            totalDespesasFixasMensal: totalDespesasUSD + convertBRLtoUSD(totalDespesasBRL, exchangeRate),
            totalDespesasFixasSemanal: despesasFixasPorSemana,
            totalFolhaMensal: folhaTotal,
            totalFolhaSemanal: saidasFolha,
            totalImpostos: saidasImpostos,
          },
        };

        weeks.push({
          weekStart: safeFormat(weekStart, 'yyyy-MM-dd'),
          weekEnd: safeFormat(weekEnd, 'yyyy-MM-dd'),
          weekLabel: `Sem ${i + 1}`,
          saldoInicial: Math.round(saldoAnterior),
          entradasAVencer: Math.round(entradasAVencerSemana),
          entradasAtraso40: Math.round(atraso40PorSemana),
          entradasNovasVendas: Math.round(entradasNovasVendas),
          totalEntradas: Math.round(totalEntradas),
          saidasFolha: Math.round(saidasFolha),
          saidasFixas: Math.round(despesasFixasSemana),
          saidasFornecedores: Math.round(saidasFornecedores),
          saidasImpostos: Math.round(saidasImpostos),
          totalSaidas: Math.round(totalSaidas),
          saldoFinal: Math.round(saldoFinal),
          usandoDreProjetado,
          details: weekDetails,
        });

        saldoAnterior = saldoFinal;
      }

      // 8. Gerar alertas
      const alerts: CashFlowAlert[] = [];
      weeks.forEach((week, i) => {
        if (week.saidasImpostos > 0) {
          alerts.push({
            type: 'warning',
            message: `Semana ${i + 1}: Saída de impostos estimada (-$${(week.saidasImpostos / 1000).toFixed(0)}K)`,
            week: i + 1,
          });
        }
        if (week.saldoFinal < week.saldoInicial * 0.7) {
          alerts.push({
            type: 'danger',
            message: `Semana ${i + 1}: Saldo cairá ${Math.round((1 - week.saldoFinal / week.saldoInicial) * 100)}%`,
            week: i + 1,
          });
        }
        if (week.saldoFinal < 0) {
          alerts.push({
            type: 'danger',
            message: `Semana ${i + 1}: Saldo projetado negativo!`,
            week: i + 1,
          });
        }
      });

      // 9. Recomendações
      const recommendations: CashFlowRecommendation[] = [];
      
      // Info sobre métricas sendo usadas
      recommendations.push({
        icon: '📊',
        message: `Usando métricas reais: ${(metrics.curvaRecebimento.semana0 * 100).toFixed(0)}% paga na 1ª semana`,
      });
      
      // Alerta se não está usando DRE Projetado
      if (!usandoDreProjetado) {
        recommendations.push({
          icon: '📝',
          message: 'Configure o DRE Projetado para projeções mais precisas de vendas novas',
        });
        if (avgMonthlySales > 0) {
          recommendations.push({
            icon: '📈',
            message: `Média histórica de recebimentos: $${(avgMonthlySales / 1000).toFixed(0)}K/mês`,
          });
        }
      } else {
        recommendations.push({
          icon: '✅',
          message: `Vendas novas baseadas no DRE Projetado: $${(receitaMensalProjetada / 1000).toFixed(0)}K/mês`,
        });
      }
      
      if (emAtrasoTotal > 100000) {
        recommendations.push({
          icon: '📞',
          message: `Intensificar cobrança dos $${(emAtrasoTotal / 1000).toFixed(0)}K em atraso (projetando ${(taxaRecebimentoEmAtraso * 100).toFixed(0)}% de recuperação)`,
        });
      }
      recommendations.push({
        icon: '💰',
        message: 'Priorizar acordos com pagamento à vista',
      });
      if (alerts.some(a => a.type === 'danger')) {
        recommendations.push({
          icon: '⏸️',
          message: 'Adiar compras não essenciais para semanas com mais folga',
        });
      }

      return {
        saldoAtual,
        weeks,
        alerts,
        recommendations,
        totalEmAtraso: emAtrasoTotal,
        totalAVencer: aVencerTotal,
        metricsUsed: {
          taxaRecebimentoAVencer,
          taxaRecebimentoEmAtraso,
          mediaSemanal: metrics.mediaSemanalRecebida,
        },
      };
    },
    staleTime: 60000,
  });
}

// Função auxiliar para calcular métricas de pagamento
async function calculatePaymentMetrics(businessUnit: BusinessUnit, brand: Brand) {
  const today = new Date();
  const startDate = safeFormat(subDays(today, 180), 'yyyy-MM-dd');
  
  let query = supabase
    .from('payments')
    .select(`
      id,
      due_date,
      paid_at,
      paid_amount,
      amount,
      deals!inner(business_unit, brand)
    `)
    .eq('status', 'paid')
    .gte('paid_at', startDate)
    .not('paid_at', 'is', null)
    .not('due_date', 'is', null);

  if (businessUnit !== 'all') {
    query = query.eq('deals.business_unit', businessUnit);
  }
  if (brand !== 'all') {
    query = query.eq('deals.brand', brand);
  }

  const { data: payments } = await query;

  // Default metrics
  const defaultMetrics = {
    curvaRecebimento: {
      semana0: 0.60,
      semana1: 0.20,
      semana2: 0.10,
      semana3: 0.05,
      semana4Plus: 0.05,
    },
    cenarios: {
      pessimista: { taxaRecebimento: 0.40 },
      realista: { taxaRecebimento: 0.60 },
      otimista: { taxaRecebimento: 0.80 },
    },
    mediaSemanalRecebida: 20000,
  };

  if (!payments || payments.length === 0) {
    return defaultMetrics;
  }

  // Calcular dias de atraso
  const pagamentosComAtraso = payments.map(p => {
    const dueDate = new Date(p.due_date!);
    const paidDate = new Date(p.paid_at!);
    const diasAtraso = Math.max(0, Math.floor((paidDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));
    return { ...p, diasAtraso, valorPago: p.paid_amount || 0 };
  });

  const total = pagamentosComAtraso.length;

  // Curva de recebimento
  const semana0 = pagamentosComAtraso.filter(p => p.diasAtraso <= 7).length / total;
  const semana1 = pagamentosComAtraso.filter(p => p.diasAtraso > 7 && p.diasAtraso <= 14).length / total;
  const semana2 = pagamentosComAtraso.filter(p => p.diasAtraso > 14 && p.diasAtraso <= 21).length / total;
  const semana3 = pagamentosComAtraso.filter(p => p.diasAtraso > 21 && p.diasAtraso <= 28).length / total;
  const semana4Plus = pagamentosComAtraso.filter(p => p.diasAtraso > 28).length / total;

  // Média semanal (últimas 8 semanas)
  const last56Days = safeFormat(subDays(today, 56), 'yyyy-MM-dd');
  const pagamentosRecentes = pagamentosComAtraso.filter(p => p.paid_at && p.paid_at >= last56Days);
  const valorRecente = pagamentosRecentes.reduce((sum, p) => sum + p.valorPago, 0);
  const mediaSemanalRecebida = valorRecente / 8;

  // Taxa até 30 dias para cenários
  const taxaAte30Dias = pagamentosComAtraso.filter(p => p.diasAtraso <= 30).length / total;

  return {
    curvaRecebimento: {
      semana0,
      semana1,
      semana2,
      semana3,
      semana4Plus,
    },
    cenarios: {
      pessimista: { taxaRecebimento: Math.max(0.3, taxaAte30Dias * 0.7) },
      realista: { taxaRecebimento: taxaAte30Dias },
      otimista: { taxaRecebimento: Math.min(1, taxaAte30Dias * 1.15) },
    },
    mediaSemanalRecebida: Math.round(mediaSemanalRecebida),
  };
}

// Hook para dados do gráfico de evolução
export function useCashFlowChart(
  businessUnit: BusinessUnit = 'all',
  daysBack: number = 30
) {
  return useQuery({
    queryKey: ['cash-flow-chart', businessUnit, daysBack],
    queryFn: async () => {
      // 1. Buscar saldo atual das contas
      const { data: bankData } = await supabase
        .from('bank_accounts')
        .select('current_balance')
        .eq('is_active', true);

      const saldoAtual = bankData?.reduce((sum, acc) => sum + (acc.current_balance || 0), 0) || 0;

      // 2. Buscar pagamentos recebidos nos últimos 30 dias
      const startDate = safeFormat(subDays(new Date(), daysBack), 'yyyy-MM-dd');
      let paymentsQuery = supabase
        .from('payments')
        .select('paid_at, paid_amount, deals!inner(business_unit)')
        .eq('status', 'paid')
        .gte('paid_at', startDate)
        .order('paid_at', { ascending: true });

      if (businessUnit !== 'all') {
        paymentsQuery = paymentsQuery.eq('deals.business_unit', businessUnit);
      }

      const { data: payments } = await paymentsQuery;

      // 3. Agrupar pagamentos por dia
      const paymentsByDay = new Map<string, number>();
      payments?.forEach(p => {
        if (p.paid_at) {
          const day = safeFormat(new Date(p.paid_at), 'yyyy-MM-dd');
          paymentsByDay.set(day, (paymentsByDay.get(day) || 0) + (p.paid_amount || 0));
        }
      });

      // 4. Calcular evolução retroativa do saldo
      // Começamos do saldo atual e voltamos no tempo subtraindo os recebimentos
      const chartData = [];
      let saldoRetroativo = saldoAtual;

      // Primeiro, calcular todos os pontos de trás para frente
      const points: { date: Date; saldo: number }[] = [];
      
      for (let i = 0; i < daysBack; i++) {
        const date = subDays(new Date(), i);
        const dayStr = safeFormat(date, 'yyyy-MM-dd');
        const recebidoNoDia = paymentsByDay.get(dayStr) || 0;
        
        points.unshift({ date, saldo: saldoRetroativo });
        saldoRetroativo -= recebidoNoDia;
      }

      // Agora converter para o formato do gráfico
      points.forEach(p => {
        chartData.push({
          date: safeFormat(p.date, 'dd'),
          saldo: Math.round(Math.max(p.saldo, 0)),
        });
      });

      return chartData;
    },
    staleTime: 120000,
  });
}
